import { useMutation, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { staffApi } from "./staffApi";
import { Contract, Paginated } from "../types/dtos";

export type ContractListParams = { page?: number; pageSize?: number; q?: string; status?: string };

async function fetchContracts(params: ContractListParams): Promise<Paginated<Contract>> {
  const res = await staffApi.get("/contracts", { params });
  return res.data;
}

export function useContracts(params: ContractListParams): UseQueryResult<Paginated<Contract>> {
  return useQuery({
    queryKey: ["contracts", params],
    queryFn: () => fetchContracts(params),
    placeholderData: (prev: Paginated<Contract> | undefined) => prev,
  });
}

export type ContractFormInput = {
  customerId: number;
  planId: number;
  billingDay: number;
  monthlyValue?: number;
  readjustmentIndex?: "IPCA" | "IGPM" | "NONE";
};

export function useCreateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContractFormInput) => staffApi.post("/contracts", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contracts"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}

export type ContractUpdateInput = Partial<Pick<ContractFormInput, "billingDay" | "monthlyValue" | "readjustmentIndex">>;

export function useUpdateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: ContractUpdateInput }) => staffApi.patch(`/contracts/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contracts"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}

export async function openContractPdf(id: number): Promise<void> {
  const res = await staffApi.get(`/contracts/${id}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data as Blob);
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function useContractTransition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: number; action: "suspend" | "reactivate" | "cancel" }) =>
      staffApi.post(`/contracts/${id}/${action}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contracts"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}
